"use client";

import React, { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="uppercase font-default text-xs font-semibold tracking-widest text-foreground hover:text-primary transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-8">
        <div className="h-6 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-32 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-6 mt-8">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="uppercase font-default text-lg font-bold tracking-widest text-foreground hover:text-primary transition-colors">
          {item.label}
        </Link>)}
      <div className="pt-8 border-t border-border mt-4" data-index="cta">
        <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-white uppercase tracking-widest text-xs font-default font-bold py-6 rounded-md">
          Schedule a Visit
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="265036" id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm" : ""}`}>
      <div className="container mx-auto px-4 h-24 flex items-center justify-between">
        {}
        <div className="md:hidden flex-1 flex justify-start">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent/50">
                <Menu className="size-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-background border-r border-border w-[300px] sm:w-[400px]">
              <div className="flex justify-start mb-8 mt-4">
                <WvcLogo className="h-8 w-auto text-primary" />
              </div>
              <MenuProvider menu_id="64">
                <MobileMenuConsumer />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-start">
          <MenuProvider menu_id="64">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex-shrink-0 flex justify-center">
          <Link to="/">
            <WvcLogo className="h-10 w-auto transition-transform duration-300 hover:scale-105 text-primary" />
          </Link>
        </div>

        {}
        <div className="flex-1 flex justify-end">
          <Button variant="outline" className="hidden md:inline-flex border-primary text-primary hover:bg-primary hover:text-white uppercase tracking-widest text-xs font-default font-bold px-8 py-5 transition-colors duration-300 rounded-md shadow-sm">
            Schedule a Visit
          </Button>
        </div>
      </div>
    </section>;
}